import { useMemo, type FC } from "react";
import { Link } from "react-router-dom";
import styles from "./Popular.module.css";
import ImageWithPlaceholder from "./ImageWithPlaceholder";
import { EventModel } from "../models/EventModel";
import { getVenueLabel } from "../utils/venueUtils";

type PopularProps = {
    events: EventModel[];
    loading?: boolean;
    maxItems?: number;
    title?: string;
    subtitle?: string;
    onEventClick?: () => void;
};

export const Popular: FC<PopularProps> = ({
    events,
    loading = false,
    maxItems = 3,
    title = "En vedette",
    subtitle = "",
    onEventClick
}) => {
    
    const featuredEvents = useMemo(() => events.slice(0, maxItems), [events, maxItems]);

    if (featuredEvents.length === 0 && !loading) {
        return null;
    }

    return (
        <section className={styles.popular} aria-labelledby="popular-heading">
            <div className={styles.header}>
                    <h2 id="popular-heading" className={styles.title}>{title}</h2>
                <p className={styles.subtitle}>{subtitle}</p>
            </div>

            <div className={styles.list}>
                {loading && featuredEvents.length === 0 && Array.from({ length: maxItems }).map((_, index) => (
                    <div key={`popular-skeleton-${index}`} className={styles.card} aria-hidden="true">
                        <div className={[styles.imageWrap, styles.skeletonBlock].join(' ')}>
                            <div className={styles.skeletonContent}>
                                <div className={styles.skeletonTitle} />
                                <div className={styles.skeletonMeta} />
                                <div className={[styles.skeletonMeta, styles.skeletonMetaShort].join(' ')} />
                            </div>
                        </div>
                    </div>
                ))}
                {featuredEvents.map((event) => {
                    const venueLabel = getVenueLabel(event.venue, event.locationName || '');
                    return (
                        <Link key={event.id} to={`/details/${event.id}`} className={styles.card} onClick={onEventClick}>
                            <div className={styles.imageWrap}>
                                <ImageWithPlaceholder className={styles.image} src={event.imageUrl} alt={event.title} />
                                <div className={styles.overlay} />
                                <div className={styles.content}>
                                    <h3 className={styles.cardTitle}>{event.title}</h3>
                                    {venueLabel && <div className={styles.metaLine}>{venueLabel}</div>}
                                    {event.date && <div className={styles.metaLine}>{event.date}</div>}
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </section>
    );
};
